// Alertas IA - Bloco 8
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  AlertTriangle, AlertCircle, Info, Bell, ChevronRight,
  ExternalLink, Loader2, Brain
} from 'lucide-react';

const API_URL = window.location.origin;

const PRIORIDADE_CONFIG = {
  critico: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50 border-red-200', badge: 'bg-red-500' },
  urgente: { icon: AlertCircle, color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200', badge: 'bg-orange-500' },
  atencao: { icon: Info, color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200', badge: 'bg-yellow-500' },
  informativo: { icon: Info, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', badge: 'bg-blue-500' }
};

export function AlertasIA({ year, trimestre }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todos');

  useEffect(() => {
    loadData();
  }, [year, trimestre]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pendencias-academicas/alertas?year=${year}&trimestre=${encodeURIComponent(trimestre)}&limit=50`);
      const json = await res.json();
      setData(json);
    } catch (error) {
      console.error('Erro ao carregar alertas:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!data) return null;

  const { alertas, por_prioridade } = data;

  // Filtrar alertas
  const alertasFiltrados = filtro === 'todos' 
    ? alertas 
    : alertas.filter(a => a.prioridade === filtro);

  return (
    <div className="space-y-6" data-testid="alertas-ia">
      {/* Header */}
      <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0 shadow-lg">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Brain className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Alertas Inteligentes</h2>
                <p className="text-purple-200">Monitoramento automático de pendências críticas</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">{data.total}</div>
              <div className="text-sm text-purple-200">alertas ativos</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumo por Prioridade */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md border-2 ${filtro === 'critico' ? 'ring-2 ring-red-500' : ''} bg-red-50 border-red-200`}
          onClick={() => setFiltro(filtro === 'critico' ? 'todos' : 'critico')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-red-700">Crítico</div>
                <div className="text-3xl font-bold text-red-600">{por_prioridade.critico}</div>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md border-2 ${filtro === 'urgente' ? 'ring-2 ring-orange-500' : ''} bg-orange-50 border-orange-200`}
          onClick={() => setFiltro(filtro === 'urgente' ? 'todos' : 'urgente')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-orange-700">Urgente</div>
                <div className="text-3xl font-bold text-orange-600">{por_prioridade.urgente}</div>
              </div>
              <AlertCircle className="w-8 h-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md border-2 ${filtro === 'atencao' ? 'ring-2 ring-yellow-500' : ''} bg-yellow-50 border-yellow-200`}
          onClick={() => setFiltro(filtro === 'atencao' ? 'todos' : 'atencao')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-yellow-700">Atenção</div>
                <div className="text-3xl font-bold text-yellow-600">{por_prioridade.atencao}</div>
              </div>
              <Bell className="w-8 h-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md border-2 ${filtro === 'informativo' ? 'ring-2 ring-blue-500' : ''} bg-blue-50 border-blue-200`}
          onClick={() => setFiltro(filtro === 'informativo' ? 'todos' : 'informativo')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-blue-700">Informativo</div>
                <div className="text-3xl font-bold text-blue-600">{por_prioridade.informativo}</div>
              </div>
              <Info className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Alertas */}
      <div className="space-y-3">
        {alertasFiltrados.length === 0 ? (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6 pb-6 text-center">
              <div className="text-green-600 text-lg font-medium">
                {filtro === 'todos' ? 'Nenhum alerta ativo!' : `Nenhum alerta ${filtro}`}
              </div>
              <p className="text-green-500 text-sm mt-1">
                A situação está sob controle.
              </p>
            </CardContent>
          </Card>
        ) : (
          alertasFiltrados.map((alerta, idx) => {
            const config = PRIORIDADE_CONFIG[alerta.prioridade] || PRIORIDADE_CONFIG.informativo;
            const Icon = config.icon;
            
            return (
              <Card key={idx} className={`${config.bg} border-2 transition-all hover:shadow-md`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${config.bg}`}>
                      <Icon className={`w-5 h-5 ${config.color}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={config.badge}>
                          {alerta.prioridade}
                        </Badge>
                        <span className="font-semibold text-slate-700">{alerta.titulo}</span>
                      </div>
                      <p className="text-slate-600 text-sm">{alerta.mensagem}</p>
                    </div>
                    {alerta.acao && (
                      <Button variant="outline" size="sm" className="shrink-0" asChild>
                        <a href={alerta.acao}>
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Ver
                        </a>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
